import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CloseIcon from "@mui/icons-material/Close";
import ArticleIcon from "@mui/icons-material/Article";
import VisibilityIcon from "@mui/icons-material/Visibility";
import UploadIcon from "@mui/icons-material/Upload";
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from "@mui/icons-material/Search";
import DownloadIcon from "@mui/icons-material/Download";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { alpha, useTheme } from "@mui/material/styles";
import { FilterColumn } from "components/StripedDataGrid/FilterColumn";
import NavigationHeader from "components/NavigationHeader";
import {
  ISolicitacaoTipologiaDetalhe,
  ISolicitacaoTipologiaDetalheItem,
  SolicitacaoTipologiaAcao,
  SolicitacaoTipologiaSituacao,
  SolicitacaoSistema,
} from "shared/interfaces/solicitacao";
import {
  atualizarSituacaoSolicitacao,
  baixarDocumentoTecnico,
  obterSolicitacao,
  removerTipologiaDaSolicitacao,
  uploadHtmlSolicitacao,
} from "services/modules/solicitacoes";
import { getEmpresaById } from "services/modules/empresas";
import { getMeusCadastrosN2 } from "services/modules/cadastrosN2";
import { getTipologiaImagens } from "services/modules/tipologias";
import { ITipologiaImagem } from "shared/interfaces/tipologia";
import { useNavigation } from "contexts/notificationsContext";
import { debounce } from "lodash";
import { TIME_DEBOUNCE } from "hooks/usePagination";
import configs from "Config";

const decodeLegacyText = (value?: string | null): string => {
  if (!value) return "";
  
  let result = value;
  
  // Substituir padrões conhecidos de encoding corrompido em português
  const replacements: Array<[RegExp | string, string]> = [
    // Padrões comuns com "�" (U+FFFD)
    [/A\uFFFD\uFFFDO/gi, "AÇÃO"],
    [/a\uFFFD\uFFFDo/gi, "ação"],
    [/\uFFFD\uFFFDO/gi, "ÇÃO"],
    [/\uFFFD\uFFFDo/gi, "ção"],
    [/ATUALIZA\uFFFD\uFFFDO/gi, "ATUALIZAÇÃO"],
    [/atualiza\uFFFD\uFFFDo/gi, "atualização"],
    [/\uFFFD/g, ""], // remover caracteres inválidos restantes
    // UTF-8 mal interpretado como Latin-1
    ["Ã§", "ç"],
    ["Ã£", "ã"],
    ["Ãµ", "õ"],
    ["Ã¡", "á"],
    ["Ã©", "é"],
    ["Ã­", "í"],
    ["Ã³", "ó"],
    ["Ãº", "ú"],
    ["Ã¢", "â"],
    ["Ãª", "ê"],
    ["Ã´", "ô"],
    ["Ã ", "à"],
    ["Ã¨", "è"],
    ["Ã¬", "ì"],
    ["Ã²", "ò"],
    ["Ã¹", "ù"],
    ["Ã±", "ñ"],
    ["Ã¼", "ü"],
  ];
  
  for (const [pattern, replacement] of replacements) {
    if (typeof pattern === "string") {
      result = result.split(pattern).join(replacement);
    } else {
      result = result.replace(pattern, replacement);
    }
  }
  
  // Se não encontrou caracteres de alto código, tentar decodificar
  const hasHighCodePoint = Array.from(result).some(
    (char) => char.charCodeAt(0) > 255
  );
  if (hasHighCodePoint) {
    return result;
  }
  
  try {
    const encoded = Array.from(result)
      .map((char) => `%${char.charCodeAt(0).toString(16).padStart(2, "0")}`)
      .join("");
    return decodeURIComponent(encoded);
  } catch {
    return result;
  }
};

const situacaoLabels: Record<SolicitacaoTipologiaSituacao, string> = {
  [SolicitacaoTipologiaSituacao.Validacao]: "Validação",
  [SolicitacaoTipologiaSituacao.Homologacao]: "Homologação",
  [SolicitacaoTipologiaSituacao.Homologada]: "Homologada",
  [SolicitacaoTipologiaSituacao.DisponivelMercado]: "Disponível Mercado",
  [SolicitacaoTipologiaSituacao.Congelada]: "Congelada",
};

// Label especial para "Excluída do Pacote" (não é um valor do enum)
const SITUACAO_EXCLUIDA_DO_PACOTE = "Excluída do Pacote";

const itemSituacaoLabels: Record<number, string> = {
  1: "Aguardando Validação",
  2: "Em Validação",
  3: "Reprovado",
  4: "Homologação Pendente",
  5: "Homologado",
  6: "Disponibilizado",
  7: "Ativo",
  8: "Descontinuado",
  9: "Liberado para uso",
};

const situacaoChipColorMap: Record<
  SolicitacaoTipologiaSituacao,
  "default" | "primary" | "secondary" | "error" | "warning" | "info" | "success"
> = {
  [SolicitacaoTipologiaSituacao.Validacao]: "warning",
  [SolicitacaoTipologiaSituacao.Homologacao]: "info",
  [SolicitacaoTipologiaSituacao.Homologada]: "success",
  [SolicitacaoTipologiaSituacao.DisponivelMercado]: "primary",
  [SolicitacaoTipologiaSituacao.Congelada]: "default",
};

const formatoData = (data: string) => {
  if (!data) return "-";
  const parsed = new Date(data);
  if (Number.isNaN(parsed.getTime())) {
    return data;
  }
  return parsed.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
};

const buildImageSrc = (url?: string | null): string => {
  if (!url) return "";
  const trimmed = url.trim();
  if (trimmed.startsWith("data:")) return trimmed;
  if (trimmed.startsWith("http://") || trimmed.startsWith("https://")) return trimmed;

  const baseUrl = configs.REACT_APP_BASE_URL?.replace("/api/", "") || "http://localhost:3099";

  // Se já contém query string (ex: /api/Pacote/imagem?path=...), usar como está
  if (trimmed.includes("?path=") || trimmed.includes("/api/Pacote/imagem")) {
    if (trimmed.startsWith("/")) {
      return `${baseUrl}${trimmed}`;
    }
    return `${baseUrl}/${trimmed}`;
  }

  // Se é apenas um nome de arquivo (sem caminho, sem "/"), provavelmente é uma imagem de componente/perfil
  // Montar URL usando o endpoint /api/Pacote/imagem
  if (!trimmed.includes("/") && !trimmed.startsWith("/")) {
    const fileName = trimmed.toLowerCase();
    
    // Detectar tipo de imagem pelo nome do arquivo
    // Componentes geralmente começam com "P-", "fc_", "RCP-", etc.
    const isComponente = fileName.startsWith("p-") || 
                         fileName.startsWith("fc_") || 
                         fileName.startsWith("rcp-") ||
                         fileName.startsWith("p-con-") ||
                         fileName.startsWith("p-fec-") ||
                         fileName.startsWith("p-fit-") ||
                         fileName.startsWith("p-bot-") ||
                         fileName.startsWith("p-capa-") ||
                         fileName.startsWith("p-cfec-") ||
                         fileName.startsWith("p-gua-");
    
    // Perfis geralmente começam com "fp_" ou são códigos de perfil (ex: CHR058, CHR-IPC200)
    // Se não for claramente um componente, assume que é perfil
    const isPerfil = fileName.startsWith("fp_") || !isComponente;
    
    let path: string;
    if (isComponente) {
      path = `assets/figuras_componente/${trimmed}`;
    } else {
      // Assume perfil se não for componente
      path = `assets/figuras_perfil/${trimmed}`;
    }
    
    const encodedPath = encodeURIComponent(path);
    return `${baseUrl}/api/Pacote/imagem?path=${encodedPath}`;
  }

  if (trimmed.startsWith("/")) {
    return `${baseUrl}${trimmed}`;
  }
  return `${baseUrl}/${trimmed}`;
};

const getCroquiUrl = (
  item: ISolicitacaoTipologiaDetalheItem,
  imagens?: ITipologiaImagem[]
): string | null => {
  if (item.urlDesenhoSvg) {
    return buildImageSrc(item.urlDesenhoSvg);
  }

  if (imagens && imagens.length > 0) {
    const croquiImagem = imagens.find((img) =>
      img.tipoImagem?.toLowerCase().includes("croqui") ||
      img.tipoImagem?.toLowerCase().includes("desenho") ||
      img.tipoImagem?.toLowerCase().includes("croquis")
    ) || imagens[0];

    if (croquiImagem?.urlImagem) {
      return buildImageSrc(croquiImagem.urlImagem);
    }
  }

  if (item.nomeArquivoDesenho) {
    const baseUrl = configs.REACT_APP_BASE_URL?.replace("/api/", "") || "";
    const encodedPath = encodeURIComponent(`assets/croquis/${item.nomeArquivoDesenho}`);
    return `${baseUrl}/api/Pacote/imagem?path=${encodedPath}`;
  }

  return null;
};

const SolicitacaoDetalhePage = () => {
  const { id } = useParams<{ id: string }>();
  const solicitacaoId = id ? Number(id) : null;
  const { openNotification } = useNavigation();
  const theme = useTheme();
  const navigate = useNavigate();

  const [modoValidacao, setModoValidacao] = useState(false);
  const [modoHomologacao, setModoHomologacao] = useState(false);
  const [modoEnvio, setModoEnvio] = useState(false);

  const [detalhe, setDetalhe] = useState<ISolicitacaoTipologiaDetalhe | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [processingSituacao, setProcessingSituacao] = useState<SolicitacaoTipologiaAcao | null>(null);
  const [removendoTipologia, setRemovendoTipologia] = useState<number | null>(null);
  const [imagensCache, setImagensCache] = useState<Map<number, ITipologiaImagem[]>>(new Map());
  const imagensCacheRef = useRef(imagensCache);
  const [cemTab, setCemTab] = useState<"cemPro" | "cemMobi">("cemPro");
  const [vinculoSelecionado, setVinculoSelecionado] = useState<number | "">("");
  const [mostrarSomenteRemovidas, setMostrarSomenteRemovidas] = useState(false);
  const [confirmacaoRemocao, setConfirmacaoRemocao] = useState<{
    open: boolean;
    itemId: number | null;
    codigo: string;
    descricao: string;
  }>({ open: false, itemId: null, codigo: "", descricao: "" });
  const [filtrosColunas, setFiltrosColunas] = useState({
    codigo: "",
    descricao: "",
    projetista: "",
    linha: "",
    status: "",
    dataAtlz: "",
  });

  const refFiltroCodigo = useRef<HTMLInputElement>(null);
  const refFiltroDescricao = useRef<HTMLInputElement>(null);
  const refFiltroProjetista = useRef<HTMLInputElement>(null);
  const refFiltroLinha = useRef<HTMLInputElement>(null);
  const refFiltroStatus = useRef<HTMLInputElement>(null);
  const refFiltroDataAtlz = useRef<HTMLInputElement>(null);

  useEffect(() => {
    imagensCacheRef.current = imagensCache;
  }, [imagensCache]);

  const carregarDetalhe = useCallback(async () => {
    if (!solicitacaoId) return;
    setIsLoading(true);
    const data = await obterSolicitacao(solicitacaoId);
    if (data) {
      setDetalhe(data);
      
      // Configurações de Cadastros N2 da própria empresa (id vem do token). O endpoint por id
      // exige a tela Configuracao (Gerência): com 403, os três modos caíam para false e os botões
      // de validar/homologar sumiam para o usuário de N2 (plano de 22/09/2026, Frente 1).
      const cadastrosN2 = await getMeusCadastrosN2();
      if (cadastrosN2) {
        setModoValidacao(cadastrosN2.modoValidacao ?? false);
        setModoHomologacao(cadastrosN2.modoHomologacao ?? false);
        setModoEnvio(cadastrosN2.modoEnvio ?? false);
      }

      setIsLoading(false);

      const buscarImagens = async () => {
        const novasImagens = new Map<number, ITipologiaImagem[]>();
        const promises = data.tipologias
          .filter((item) => !imagensCacheRef.current.has(item.tipologiaId))
          .map(async (item) => {
            try {
              const imagens = await getTipologiaImagens(item.tipologiaId);
              if (imagens && imagens.length > 0) {
                novasImagens.set(item.tipologiaId, imagens);
              }
            } catch (error) {
              console.error(`Erro ao buscar imagens da tipologia ${item.tipologiaId}:`, error);
            }
          });

        await Promise.all(promises);

        if (novasImagens.size > 0) {
          setImagensCache((prev) => new Map([...prev, ...novasImagens]));
        }
      };

      buscarImagens();
    } else {
      setIsLoading(false);
    }
  }, [solicitacaoId]);

  useEffect(() => {
    if (solicitacaoId) {
      carregarDetalhe();
      setCemTab("cemPro");
      setVinculoSelecionado("");
      setMostrarSomenteRemovidas(false);
      setFiltrosColunas({
        codigo: "",
        descricao: "",
        projetista: "",
        linha: "",
        status: "",
        dataAtlz: "",
      });
      setImagensCache(new Map());
    }
  }, [solicitacaoId, carregarDetalhe]);

  const debouncedSetFiltro = useCallback(
    debounce((campo: string, valor: string) => {
      setFiltrosColunas((prev) => ({ ...prev, [campo]: valor }));
    }, TIME_DEBOUNCE),
    []
  );

  const temDescontinuados = useMemo(() => {
    if (!detalhe) return false;
    return detalhe.tipologias.some((t) => t.descontinuado && t.situacao !== 3);
  }, [detalhe]);

  const podeValidar = useMemo(() => {
    if (!detalhe || detalhe.congelada) return false;
    if (temDescontinuados) return false;
    // CEM MOBI não permite validar/homologar, apenas disponibilizar mercado
    if (cemTab === "cemMobi") return false;
    return modoValidacao && detalhe.situacao === SolicitacaoTipologiaSituacao.Validacao;
  }, [detalhe, modoValidacao, temDescontinuados, cemTab]);

  const podeHomologar = useMemo(() => {
    if (!detalhe || detalhe.congelada) return false;
    if (temDescontinuados) return false;
    // CEM MOBI não permite validar/homologar, apenas disponibilizar mercado
    if (cemTab === "cemMobi") return false;
    return modoHomologacao && detalhe.situacao === SolicitacaoTipologiaSituacao.Homologacao;
  }, [detalhe, modoHomologacao, temDescontinuados, cemTab]);

  const podeDisponibilizar = useMemo(() => {
    if (!detalhe || detalhe.congelada) return false;
    if (temDescontinuados) return false;

    // Para cada aba, usamos a situação específica do sistema correspondente
    const situacaoSistemaAtual =
      cemTab === "cemMobi" ? detalhe.situacaoCemMobi : detalhe.situacaoCemDelphi;

    return modoValidacao && situacaoSistemaAtual === SolicitacaoTipologiaSituacao.Homologada;
  }, [detalhe, modoValidacao, temDescontinuados, cemTab]);

  const tipologiasFiltradas = useMemo(() => {
    if (!detalhe) return [];

    let tipologias = detalhe.tipologias;

    if (vinculoSelecionado !== "") {
      // critério futuro - atualmente mantemos todas
    }

    if (mostrarSomenteRemovidas) {
      tipologias = tipologias.filter((t) => t.substituida || t.situacao === 3);
    }

    if (filtrosColunas.codigo) {
      const filtro = filtrosColunas.codigo.toLowerCase();
      tipologias = tipologias.filter((t) => t.codigo?.toLowerCase().includes(filtro));
    }
    if (filtrosColunas.descricao) {
      const filtro = filtrosColunas.descricao.toLowerCase();
      tipologias = tipologias.filter((t) => t.descricao?.toLowerCase().includes(filtro));
    }
    if (filtrosColunas.linha) {
      const filtro = filtrosColunas.linha.toLowerCase();
      tipologias = tipologias.filter((t) => t.linha?.toLowerCase().includes(filtro));
    }
    if (filtrosColunas.status) {
      const filtro = filtrosColunas.status.toLowerCase();
      tipologias = tipologias.filter((t) => {
        const situacaoItem = cemTab === "cemMobi" 
          ? (t.situacaoCemMobi ?? t.situacao)
          : (t.situacaoCemDelphi ?? t.situacao);
        const statusLabel = itemSituacaoLabels[situacaoItem] || "";
        return statusLabel.toLowerCase().includes(filtro);
      });
    }
    if (filtrosColunas.dataAtlz) {
      const filtro = filtrosColunas.dataAtlz.toLowerCase();
      tipologias = tipologias.filter((t) => {
        const dataFormatada = t.dataVersao ? formatoData(t.dataVersao) : "";
        return dataFormatada.toLowerCase().includes(filtro);
      });
    }

    return tipologias;
  }, [detalhe, vinculoSelecionado, mostrarSomenteRemovidas, filtrosColunas]);


  const executarAcaoSituacaoInterno = async (acao: SolicitacaoTipologiaAcao) => {
    if (!detalhe) return;

    // Converter string para enum numérico (backend espera números: 1 = CemDelphi, 2 = CemMobi)
    const sistema =
      cemTab === "cemMobi" ? SolicitacaoSistema.CemMobi : SolicitacaoSistema.CemDelphi;

    setProcessingSituacao(acao);
    const sucesso = await atualizarSituacaoSolicitacao(
      detalhe.idSolicitacao,
      acao,
      sistema,
    );
    setProcessingSituacao(null);

    if (sucesso) {
      const mensagens: Record<SolicitacaoTipologiaAcao, string> = {
        [SolicitacaoTipologiaAcao.Validar]: "Solicitação validada com sucesso.",
        [SolicitacaoTipologiaAcao.ConcluirHomologacao]: "Homologação concluída com sucesso.",
        [SolicitacaoTipologiaAcao.DisponibilizarMercado]: "Solicitação disponibilizada ao mercado.",
      };
      openNotification({
        message: mensagens[acao],
        type: "success",
      });
      await carregarDetalhe();
    }
  };

  // Estado para modal de confirmação de homologação
  const [modalConfirmacaoHomologacaoOpen, setModalConfirmacaoHomologacaoOpen] = useState(false);
  const [empresaNome, setEmpresaNome] = useState<string>("");

  // Estado para modal de upload de HTML
  const [modalUploadHtmlOpen, setModalUploadHtmlOpen] = useState(false);
  const [htmlFile, setHtmlFile] = useState<File | null>(null);
  const [uploadingHtml, setUploadingHtml] = useState(false);
  const [sistemaParaUpload, setSistemaParaUpload] = useState<SolicitacaoSistema | null>(null);
  const [modalConfirmacaoVinculosOpen, setModalConfirmacaoVinculosOpen] = useState(false);

  const executarAcaoSituacao = async (acao: SolicitacaoTipologiaAcao) => {
    if (!detalhe) return;

    // Para concluir homologação, buscar nome da empresa e abrir modal de confirmação
    if (acao === SolicitacaoTipologiaAcao.ConcluirHomologacao) {
      try {
        const empresaResponse = await getEmpresaById(detalhe.empresaId);
        if (empresaResponse?.data) {
          const nomeEmpresa = empresaResponse.data.nomeFantasia || empresaResponse.data.razaoSocial || "Empresa";
          setEmpresaNome(nomeEmpresa);
        } else {
          setEmpresaNome("Empresa");
        }
      } catch {
        setEmpresaNome("Empresa");
      }
      setModalConfirmacaoHomologacaoOpen(true);
      return;
    }

    if (acao === SolicitacaoTipologiaAcao.DisponibilizarMercado) {
      const sistema = cemTab === "cemMobi" ? SolicitacaoSistema.CemMobi : SolicitacaoSistema.CemDelphi;

      const vinculosAtivos = detalhe.vinculos.filter(
        v => !v.atual && !v.todasTipologiasSubstituidas && !v.congelada
      );

      if (vinculosAtivos.length > 0) {
        const todasHomologadas = vinculosAtivos.every(
          v => v.situacao === SolicitacaoTipologiaSituacao.Homologada
            || v.situacao === SolicitacaoTipologiaSituacao.DisponivelMercado
        );
        if (!todasHomologadas) {
          openNotification({
            message: "Não foi possível disponibilizar para mercado. Existem solicitações vinculadas com situação diferente de HOMOLOGADA.",
            type: "error",
          });
          return;
        }
        setModalConfirmacaoVinculosOpen(true);
        return;
      }

      setSistemaParaUpload(sistema);
      setModalUploadHtmlOpen(true);
      return;
    }

    // Executar ação diretamente
    await executarAcaoSituacaoInterno(acao);
  };

  const handleUploadHtml = async () => {
    if (!detalhe || !htmlFile || !sistemaParaUpload) return;

    setUploadingHtml(true);
    const resultado = await uploadHtmlSolicitacao(detalhe.idSolicitacao, htmlFile, sistemaParaUpload);
    setUploadingHtml(false);

    if (resultado) {
      openNotification({
        message: "Arquivo HTML enviado com sucesso.",
        type: "success",
      });
      setModalUploadHtmlOpen(false);
      setHtmlFile(null);
      setSistemaParaUpload(null);
      
      // Recarregar detalhe para atualizar informações do HTML
      await carregarDetalhe();
      
      // Agora executar a ação de disponibilizar mercado
      await executarAcaoSituacaoInterno(SolicitacaoTipologiaAcao.DisponibilizarMercado);
    }
  };

  const handleConfirmacaoVinculos = () => {
    setModalConfirmacaoVinculosOpen(false);
    const sistema = cemTab === "cemMobi" ? SolicitacaoSistema.CemMobi : SolicitacaoSistema.CemDelphi;
    setSistemaParaUpload(sistema);
    setModalUploadHtmlOpen(true);
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) {
      // Validar extensão
      if (!file.name.toLowerCase().endsWith(".html")) {
        openNotification({
          message: "Apenas arquivos .html são aceitos.",
          type: "error",
        });
        return;
      }
      // Validar tamanho (5 MB)
      const maxSize = 5 * 1024 * 1024; // 5 MB
      if (file.size > maxSize) {
        openNotification({
          message: "O arquivo HTML não pode exceder 5 MB.",
          type: "error",
        });
        return;
      }
      setHtmlFile(file);
    }
  };

  const confirmarHomologacao = async () => {
    if (!detalhe) return;
    setModalConfirmacaoHomologacaoOpen(false);
    await executarAcaoSituacaoInterno(SolicitacaoTipologiaAcao.ConcluirHomologacao);
  };


  const handleDownloadDocumentoTecnico = async () => {
    if (!detalhe) return;
    const blob = await baixarDocumentoTecnico(detalhe.idSolicitacao);
    if (!blob) {
      openNotification({
        message: "Documento técnico (PDF) não disponível para esta solicitação. O PDF só está disponível para solicitações importadas após a atualização do sistema.",
        type: "warning",
      });
      return;
    }
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    // O nome do arquivo será definido pelo Content-Disposition do backend
    link.setAttribute("download", "");
    document.body.appendChild(link);
    link.click();
    link.parentNode?.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const solicitarConfirmacaoRemocao = (item: ISolicitacaoTipologiaDetalheItem) => {
    setConfirmacaoRemocao({
      open: true,
      itemId: item.idItem,
      codigo: item.codigo,
      descricao: item.descricao || "",
    });
  };

  const confirmarRemocaoTipologia = async () => {
    if (!detalhe || !confirmacaoRemocao.itemId) return;

    setRemovendoTipologia(confirmacaoRemocao.itemId);
    setConfirmacaoRemocao({ open: false, itemId: null, codigo: "", descricao: "" });

    const sucesso = await removerTipologiaDaSolicitacao(detalhe.idSolicitacao, confirmacaoRemocao.itemId);
    setRemovendoTipologia(null);

    if (sucesso) {
      openNotification({
        message: "Tipologia excluída do pacote. Ela não será incluída na homologação.",
        type: "success",
      });
      await carregarDetalhe();
    }
  };

  const cancelarRemocaoTipologia = () => {
    setConfirmacaoRemocao({ open: false, itemId: null, codigo: "", descricao: "" });
  };

  const getRowBackgroundColor = (item: ISolicitacaoTipologiaDetalheItem) => {
    if (estaExcluidaDoPacotePorSistema(item)) {
      return alpha(theme.palette.error.main, 0.08);
    }
    const situacaoItem = getItemSituacao(item);
    if (situacaoItem === 3) {
      return alpha(theme.palette.error.main, 0.1);
    }
    if (item.descontinuado) {
      return alpha(theme.palette.warning.main, 0.1);
    }
    if (item.numeroSolicitacaoDestino) {
      return alpha(theme.palette.info.main, 0.08);
    }
    return "transparent";
  };

  const getRowTooltip = (item: ISolicitacaoTipologiaDetalheItem) => {
    if (estaExcluidaDoPacotePorSistema(item)) {
      return "Tipologia excluída do pacote - não será incluída na homologação";
    }
    const situacaoItem = getItemSituacao(item);
    if (situacaoItem === 3) {
      return "Tipologia reprovada";
    }
    if (item.numeroSolicitacaoDestino) {
      return `Tipologia atualizada na solicitação ${String(item.numeroSolicitacaoDestino).padStart(7, "0")}`;
    }
    if (item.descontinuado) {
      return "Tipologia possui elementos descontinuados";
    }
    return "";
  };

  // Função auxiliar para obter o status do item baseado na aba ativa
  const getItemSituacao = (item: ISolicitacaoTipologiaDetalheItem): number => {
    if (cemTab === "cemMobi") {
      return item.situacaoCemMobi ?? item.situacao;
    } else {
      return item.situacaoCemDelphi ?? item.situacao;
    }
  };

  // Função auxiliar para verificar se está "Excluída do pacote" por sistema
  // REGRA: Se está "Disponibilizado" para o outro sistema mas não para o atual,
  // e uma nova solicitação foi criada (numeroSolicitacaoDestino existe),
  // então o sistema atual deve ser considerado "Excluída do pacote"
  const estaExcluidaDoPacotePorSistema = (item: ISolicitacaoTipologiaDetalheItem): boolean => {
    // Se está marcado como Substituida globalmente, está excluída
    if (item.substituida) return true;

    return false;
  };

  const getStatusColor = (item: ISolicitacaoTipologiaDetalheItem) => {
    if (estaExcluidaDoPacotePorSistema(item)) return "default";
    const situacaoItem = getItemSituacao(item);
    if (situacaoItem === 3) return "error";
    if (item.descontinuado) return "warning";
    return "default";
  };

  const getStatusLabel = (item: ISolicitacaoTipologiaDetalheItem) => {
    if (estaExcluidaDoPacotePorSistema(item)) return "Excluída do Pacote";
    if (item.descontinuado) return "Descontinuada";
    
    const situacaoItem = getItemSituacao(item);
    if (situacaoItem === 3) return "Reprovada";
    return itemSituacaoLabels[situacaoItem] ?? "Ativo";
  };

  // Função auxiliar para verificar se uma tipologia está excluída do pacote para um sistema específico
  const estaExcluidaDoPacotePorSistemaEspecifico = (
    item: ISolicitacaoTipologiaDetalheItem,
    sistema: "cemPro" | "cemMobi"
  ): boolean => {
    // Se está marcado como Substituida globalmente, está excluída
    if (item.substituida) return true;

    return false;
  };

  // Função auxiliar para calcular o status do cabeçalho baseado nas tipologias para um sistema específico
  const calcularStatusCabeçalhoPorSistema = (
    sistema: "cemPro" | "cemMobi"
  ): { label: string; situacao: SolicitacaoTipologiaSituacao | null } => {
    if (!detalhe || !detalhe.tipologias || detalhe.tipologias.length === 0) {
      return {
        label: situacaoLabels[detalhe?.situacaoCemDelphi ?? SolicitacaoTipologiaSituacao.Validacao],
        situacao: detalhe?.situacaoCemDelphi ?? SolicitacaoTipologiaSituacao.Validacao,
      };
    }

    // Filtrar tipologias não excluídas do pacote para o sistema específico
    const tipologiasNaoExcluidas = detalhe.tipologias.filter(
      (item) => !estaExcluidaDoPacotePorSistemaEspecifico(item, sistema)
    );

    // Se todas as tipologias estão excluídas do pacote, exibir "Excluída do Pacote"
    if (tipologiasNaoExcluidas.length === 0) {
      return {
        label: "Excluída do Pacote",
        situacao: null,
      };
    }

    // Obter o status mais avançado das tipologias não excluídas
    const statusPorSistema = tipologiasNaoExcluidas.map((item) => {
      if (sistema === "cemPro") {
        return item.situacaoCemDelphi ?? item.situacao;
      } else {
        return item.situacaoCemMobi ?? item.situacao;
      }
    });

    // Mapear status de item para status de solicitação
    // 1 = AguardandoValidação -> Validacao
    // 2 = EmValidacao -> Validacao
    // 3 = Reprovado -> Validacao (mantém como está)
    // 4 = HomologacaoPendente -> Homologacao
    // 5 = Homologado -> Homologada
    // 6 = Disponibilizado -> DisponivelMercado
    const mapearStatusItemParaSolicitacao = (statusItem: number): SolicitacaoTipologiaSituacao => {
      if (statusItem === 6) return SolicitacaoTipologiaSituacao.DisponivelMercado;
      if (statusItem === 5) return SolicitacaoTipologiaSituacao.Homologada;
      if (statusItem === 4) return SolicitacaoTipologiaSituacao.Homologacao;
      return SolicitacaoTipologiaSituacao.Validacao;
    };

    const statusSolicitacao = statusPorSistema.map(mapearStatusItemParaSolicitacao);

    // Retornar o status mais avançado
    // Ordem: Validacao < Homologacao < Homologada < DisponivelMercado
    const statusMaisAvancado = statusSolicitacao.reduce((prev, curr) => {
      if (curr === SolicitacaoTipologiaSituacao.DisponivelMercado) return curr;
      if (curr === SolicitacaoTipologiaSituacao.Homologada && prev !== SolicitacaoTipologiaSituacao.DisponivelMercado) return curr;
      if (curr === SolicitacaoTipologiaSituacao.Homologacao && prev === SolicitacaoTipologiaSituacao.Validacao) return curr;
      return prev;
    }, SolicitacaoTipologiaSituacao.Validacao);

    return {
      label: situacaoLabels[statusMaisAvancado],
      situacao: statusMaisAvancado,
    };
  };

  // Calcular status do cabeçalho para CEM DELPHI e CEM MOBI
  const statusCabeçalhoDelphi = useMemo(
    () => calcularStatusCabeçalhoPorSistema("cemPro"),
    [detalhe, cemTab]
  );

  const statusCabeçalhoMobi = useMemo(
    () => calcularStatusCabeçalhoPorSistema("cemMobi"),
    [detalhe, cemTab]
  );

  // Função auxiliar para verificar se todas as tipologias estão "Excluída do pacote" para o sistema atual
  const todasTipologiasExcluidasDoPacote = useMemo(() => {
    if (!detalhe || !detalhe.tipologias || detalhe.tipologias.length === 0) {
      return false;
    }

    // Verificar se todas as tipologias estão excluídas do pacote para o sistema atual
    const todasExcluidas = detalhe.tipologias.every((item) => {
      return estaExcluidaDoPacotePorSistemaEspecifico(item, cemTab);
    });

    return todasExcluidas;
  }, [detalhe, cemTab]);

  const getBotaoAcaoLabel = () => {
    if (!detalhe) return null;

    if (detalhe.congelada) return null;

    if (todasTipologiasExcluidasDoPacote) return null;

    // Para cada aba, usamos a situação específica do sistema correspondente
    const situacaoSistemaAtual =
      cemTab === "cemMobi" ? detalhe.situacaoCemMobi : detalhe.situacaoCemDelphi;

    // Se CEM MOBI estiver selecionado, só mostrar "DISPONIBILIZAR MERCADO" quando estiver em Homologada
    if (cemTab === "cemMobi") {
      if (situacaoSistemaAtual === SolicitacaoTipologiaSituacao.Homologada) {
        if (!modoValidacao) return null;
        if (todasTipologiasExcluidasDoPacote) {
          return null;
        }
        return { label: "DISPONIBILIZAR MERCADO", acao: SolicitacaoTipologiaAcao.DisponibilizarMercado, color: "success" as const };
      }
      return null;
    }

    // Para CEM DELPHI (cemPro), mostrar a sequência normal usando a situação específica do sistema
    if (situacaoSistemaAtual === SolicitacaoTipologiaSituacao.Validacao) {
      if (!modoValidacao) return null;
      return { label: "SOLICITAR HOMOLOGAÇÃO", acao: SolicitacaoTipologiaAcao.Validar, color: "primary" as const };
    }
    if (situacaoSistemaAtual === SolicitacaoTipologiaSituacao.Homologacao) {
      if (!modoHomologacao) return null;
      return { label: "CONCLUIR HOMOLOGAÇÃO", acao: SolicitacaoTipologiaAcao.ConcluirHomologacao, color: "info" as const };
    }
    if (situacaoSistemaAtual === SolicitacaoTipologiaSituacao.Homologada) {
      if (!modoValidacao) return null;
      if (todasTipologiasExcluidasDoPacote) {
        return null;
      }
      return { label: "DISPONIBILIZAR MERCADO", acao: SolicitacaoTipologiaAcao.DisponibilizarMercado, color: "success" as const };
    }
    return null;
  };

  const botaoAcao = getBotaoAcaoLabel();
  const podeExecutarAcao = podeValidar || podeHomologar || podeDisponibilizar;

  if (!solicitacaoId) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography variant="h6">Solicitação inválida</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "background.default", py: 3 }}>
      <Box
        sx={{
          width: "95vw",
          maxWidth: "1400px",
          mx: "auto",
          bgcolor: "background.paper",
          borderRadius: 2,
          boxShadow: 3,
          display: "flex",
          flexDirection: "column",
          minHeight: "85vh",
        }}
      >
        <Box sx={{ px: 3, py: 2 }}>
          <NavigationHeader
            title="SOLICITAÇÕES > TIPOLOGIAS"
            label="Tipologias"
            links={[
              { label: "Solicitações", link: "/solicitacoesMof" },
            ]}
            hasBackButton={true}
            goBack={() => navigate("/solicitacoesMof")}
            customElement={
              detalhe && (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  display="flex"
                  justifyContent="flex-end"
                  alignItems="flex-start"
                  sx={{ mt: { xs: 2, sm: 0 } }}
                >
                  <Stack direction="row" spacing={4} alignItems="flex-start">
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        Solicitação
                      </Typography>
                      <Typography variant="h6" fontWeight={700}>
                        {String(detalhe.numeroSolicitacao).padStart(7, "0")}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        Descrição da solicitação
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {decodeLegacyText(detalhe.descricaoAtualizacao) || "Não informado"}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        CEM Desktop
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {statusCabeçalhoDelphi.label}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                        CEM MOBI
                      </Typography>
                      <Typography variant="body1" fontWeight={600}>
                        {statusCabeçalhoMobi.label}
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              )
            }
          />
        </Box>

        {isLoading && (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography>Carregando...</Typography>
          </Box>
        )}

        {!isLoading && detalhe && (
          <Box sx={{ flex: 1, overflow: "auto", p: 3 }}>
            {temDescontinuados && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Esta solicitação contém tipologias com elementos descontinuados. Não é possível avançar para a próxima etapa até que os perfis ou componentes descontinuados sejam corrigidos.
              </Alert>
            )}

            {detalhe.emergencial && (
              <Alert severity="error" sx={{ mb: 2 }}>
                Esta solicitação está marcada como atualização emergencial. Solicitações do mesmo projetista estão temporariamente congeladas.
              </Alert>
            )}

            {detalhe.congelada && !detalhe.emergencial && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Há uma atualização emergencial em andamento para este projetista. Esta solicitação permanecerá apenas para consulta até a conclusão da prioridade.
              </Alert>
            )}

            {detalhe.homologacaoData && detalhe.homologacaoResponsavel && (
              <Alert severity="success" sx={{ mb: 2 }}>
                <Typography variant="body2" fontWeight={600} sx={{ mb: 0.5 }}>
                  Homologação concluída
                </Typography>
                <Typography variant="body2">
                  <strong>Responsável:</strong> {detalhe.homologacaoResponsavel}
                  {detalhe.homologacaoIp && (
                    <> - <strong>IP:</strong> {detalhe.homologacaoIp}</>
                  )}
                  {detalhe.homologacaoData && (
                    <> - <strong>Data/Hora:</strong> {(() => {
                      try {
                        // A data já vem em UTC-3 do backend, então apenas formatar
                        const data = new Date(detalhe.homologacaoData);
                        return data.toLocaleString("pt-BR", {
                          day: "2-digit",
                          month: "2-digit",
                          year: "numeric",
                          hour: "2-digit",
                          minute: "2-digit",
                        });
                      } catch {
                        return detalhe.homologacaoData;
                      }
                    })()}</>
                  )}
                </Typography>
              </Alert>
            )}

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
              <Stack direction="row" spacing={0}>
                <Button
                  variant={cemTab === "cemPro" ? "contained" : "outlined"}
                  onClick={() => setCemTab("cemPro")}
                  sx={{ borderRadius: "4px 0 0 4px", textTransform: "none", fontWeight: 600, minWidth: 120 }}
                >
                  CEM Desktop
                </Button>
                <Button
                  variant={cemTab === "cemMobi" ? "contained" : "outlined"}
                  onClick={() => setCemTab("cemMobi")}
                  sx={{ borderRadius: "0 4px 4px 0", textTransform: "none", fontWeight: 600, minWidth: 120 }}
                >
                  CEM MOBI
                </Button>
              </Stack>

              <Tooltip title="Exibe apenas tipologias excluídas do pacote (não serão homologadas)">
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={mostrarSomenteRemovidas}
                      onChange={(e) => setMostrarSomenteRemovidas(e.target.checked)}
                    />
                  }
                  label="VISUALIZAR SOMENTE EXCLUÍDAS DO PACOTE"
                  sx={{
                    "& .MuiFormControlLabel-label": {
                      fontSize: "0.875rem",
                      fontWeight: 500,
                      color: "primary.main",
                    },
                  }}
                />
              </Tooltip>
            </Stack>

            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
              <FormControl size="small" sx={{ minWidth: 250 }}>
                <InputLabel>Vínculo(s)</InputLabel>
                <Select
                  value={vinculoSelecionado}
                  onChange={(e: SelectChangeEvent<number | "">) => setVinculoSelecionado(e.target.value as number | "")}
                  label="Vínculo(s)"
                  renderValue={(selected) => {
                    if (!selected && selected !== 0) return "Todos";
                    const vinculo = detalhe.vinculos.find(v => v.idSolicitacao === selected);
                    if (!vinculo) return "";
                    return `${String(vinculo.numeroSolicitacao).padStart(6, "0")} - ${situacaoLabels[vinculo.situacao]}`;
                  }}
                >
                  <MenuItem value="">
                    <em>Todos</em>
                  </MenuItem>
                  {detalhe.vinculos.map((vinculo) => (
                    <MenuItem key={vinculo.idSolicitacao} value={vinculo.idSolicitacao}>
                      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", width: "100%" }}>
                        <span>{String(vinculo.numeroSolicitacao).padStart(6, "0")} - {situacaoLabels[vinculo.situacao]}</span>
                        <IconButton
                          size="small"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/solicitacoesMof/${vinculo.idSolicitacao}`);
                          }}
                          sx={{ ml: 1, color: "#3C90AD" }}
                          title="Abrir solicitação vinculada"
                        >
                          <OpenInNewIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Stack direction="row" spacing={1.5}>
                {/* Botões de HTML - CEM DELPHI (apenas na aba CEM DELPHI) */}
                {cemTab === "cemPro" && detalhe.htmlCemDelphiUrl && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => window.open(detalhe.htmlCemDelphiUrl!, '_blank')}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      VISUALIZAR HTML DELPHI
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => {
                        setSistemaParaUpload(SolicitacaoSistema.CemDelphi);
                        setModalUploadHtmlOpen(true);
                      }}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      REENVIAR HTML DELPHI
                    </Button>
                  </>
                )}

                {/* Botões de HTML - CEM MOBI (apenas na aba CEM MOBI) */}
                {cemTab === "cemMobi" && detalhe.htmlCemMobiUrl && (
                  <>
                    <Button
                      variant="outlined"
                      startIcon={<VisibilityIcon />}
                      onClick={() => window.open(detalhe.htmlCemMobiUrl!, '_blank')}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      VISUALIZAR HTML MOBI
                    </Button>
                    <Button
                      variant="outlined"
                      startIcon={<UploadIcon />}
                      onClick={() => {
                        setSistemaParaUpload(SolicitacaoSistema.CemMobi);
                        setModalUploadHtmlOpen(true);
                      }}
                      sx={{ textTransform: "none", fontWeight: 600 }}
                    >
                      REENVIAR HTML MOBI
                    </Button>
                  </>
                )}

                <Button
                  variant="outlined"
                  startIcon={<ArticleIcon />}
                  onClick={handleDownloadDocumentoTecnico}
                  sx={{ textTransform: "none", fontWeight: 600 }}
                >
                  DOCUMENTO TÉCNICO
                </Button>

                {/* Exibir data de disponibilização se já estiver disponível, senão mostrar botão de ação */}
                {(() => {
                  const situacaoSistemaAtual =
                    cemTab === "cemMobi" ? detalhe.situacaoCemMobi : detalhe.situacaoCemDelphi;
                  const dataDisponibilizacao =
                    cemTab === "cemMobi"
                      ? detalhe.dataDisponibilizacaoCemMobi
                      : detalhe.dataDisponibilizacaoCemDelphi;

                  // Se estiver disponível para mercado, mostrar a data
                  if (situacaoSistemaAtual === SolicitacaoTipologiaSituacao.DisponivelMercado && dataDisponibilizacao) {
                    // A data vem do backend em UTC (DateTime.UtcNow), precisamos converter para o timezone local (Brasília, UTC-3)
                    // Garantir que a string seja interpretada como UTC
                    let dataUtcString = dataDisponibilizacao;
                    if (!dataUtcString.endsWith('Z') && !dataUtcString.includes('+') && !dataUtcString.includes('-', 10)) {
                      // Se não tem indicador de timezone, assumir UTC
                      dataUtcString = dataUtcString.endsWith('Z') ? dataUtcString : dataUtcString + 'Z';
                    }
                    
                    const dataUtc = new Date(dataUtcString);
                    
                    // Converter para horário de Brasília (UTC-3)
                    const dataFormatada = dataUtc.toLocaleString("pt-BR", {
                      day: "2-digit",
                      month: "2-digit",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                      timeZone: "America/Sao_Paulo",
                    });

                    return (
                      <Box
                        sx={{
                          border: "2px solid #d32f2f",
                          borderRadius: 1,
                          p: 1.5,
                          backgroundColor: "#ffebee",
                          minWidth: 280,
                        }}
                      >
                        <Typography variant="caption" color="text.secondary" sx={{ display: "block", fontSize: "0.75rem" }}>
                          Data disponibilização mercado
                        </Typography>
                        <Typography variant="body2" fontWeight={600} sx={{ color: "#d32f2f" }}>
                          {dataFormatada}
                        </Typography>
                      </Box>
                    );
                  }

                  // Caso contrário, mostrar botão de ação se existir
                  if (botaoAcao) {
                    return (
                      <Tooltip
                        title={
                          temDescontinuados
                            ? "Existem tipologias com elementos descontinuados. Corrija antes de continuar."
                            : ""
                        }
                      >
                        <span>
                          <LoadingButton
                            variant="contained"
                            color={botaoAcao.color}
                            loading={processingSituacao === botaoAcao.acao}
                            disabled={temDescontinuados}
                            onClick={() => executarAcaoSituacao(botaoAcao.acao)}
                            sx={{ textTransform: "none", fontWeight: 600, minWidth: 200 }}
                          >
                            {botaoAcao.label}
                          </LoadingButton>
                        </span>
                      </Tooltip>
                    );
                  }

                  return null;
                })()}
              </Stack>
            </Stack>

            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: "rgba(226, 232, 240, 1)", height: "56px" }}>
                    <TableCell sx={{ width: 120, py: 1.5, px: 1, bgcolor: "transparent", borderBottom: "none" }}>
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#001A29" }}>
                        Desenho
                      </Typography>
                    </TableCell>
                    <TableCell sx={{ width: 120, py: 0, px: 1.5, bgcolor: "transparent", borderBottom: "none" }}>
                      <FilterColumn
                        label="Código"
                        name="codigo"
                        inputRef={refFiltroCodigo}
                        onChange={(e) => debouncedSetFiltro("codigo", e.target.value)}
                      />
                    </TableCell>
                    <TableCell sx={{ py: 0, px: 1.5, bgcolor: "transparent", borderBottom: "none" }}>
                      <FilterColumn
                        label="Descrição"
                        name="descricao"
                        inputRef={refFiltroDescricao}
                        onChange={(e) => debouncedSetFiltro("descricao", e.target.value)}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 120, py: 0, px: 1.5, bgcolor: "transparent", borderBottom: "none" }}>
                      <FilterColumn
                        label="Projetista"
                        name="projetista"
                        inputRef={refFiltroProjetista}
                        onChange={(e) => debouncedSetFiltro("projetista", e.target.value)}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 150, py: 0, px: 1.5, bgcolor: "transparent", borderBottom: "none" }}>
                      <FilterColumn
                        label="Linha"
                        name="linha"
                        inputRef={refFiltroLinha}
                        onChange={(e) => debouncedSetFiltro("linha", e.target.value)}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 140, py: 0, px: 1.5, bgcolor: "transparent", borderBottom: "none" }}>
                      <FilterColumn
                        label="Status"
                        name="status"
                        inputRef={refFiltroStatus}
                        onChange={(e) => debouncedSetFiltro("status", e.target.value)}
                      />
                    </TableCell>
                    <TableCell sx={{ width: 110, py: 0, px: 1.5, bgcolor: "transparent", borderBottom: "none" }}>
                      <FilterColumn
                        label="Data Ult Atlz"
                        name="dataAtlz"
                        inputRef={refFiltroDataAtlz}
                        onChange={(e) => debouncedSetFiltro("dataAtlz", e.target.value)}
                      />
                    </TableCell>
                    {modoValidacao && (
                    <TableCell sx={{ width: 100, py: 1.5, px: 1, bgcolor: "transparent", borderBottom: "none" }} align="center">
                      <Typography variant="body2" sx={{ fontWeight: 600, color: "#001A29" }}>
                        Ações
                      </Typography>
                    </TableCell>
                    )}
                  </TableRow>
                </TableHead>

                <TableBody>
                  {tipologiasFiltradas.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} sx={{ textAlign: "center", py: 4 }}>
                        <Typography variant="body2" color="text.secondary">
                          {mostrarSomenteRemovidas
                            ? "Nenhuma tipologia foi excluída do pacote nesta solicitação."
                            : "Nenhuma tipologia encontrada."}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    tipologiasFiltradas.map((item) => {
                      const foiAtualizada = !!item.numeroSolicitacaoDestino && !item.substituida;
                      const rowTooltip = getRowTooltip(item);
                      const imagens = imagensCache.get(item.tipologiaId);
                      const croquiUrl = getCroquiUrl(item, imagens);

                      return (
                        <Tooltip key={item.idItem} title={rowTooltip} placement="top" arrow>
                          <TableRow
                            sx={{
                              bgcolor: getRowBackgroundColor(item),
                              "&:hover": {
                                bgcolor: alpha(theme.palette.primary.main, 0.05),
                              },
                            }}
                          >
                            <TableCell>
                              <div style={{ padding: "4px", textAlign: "center" }}>
                                {croquiUrl ? (
                                  <Tooltip
                                    title={
                                      <Box
                                        component="img"
                                        src={croquiUrl}
                                        alt={item.codigo}
                                        sx={{
                                          width: "230px",
                                          maxHeight: "230px",
                                          objectFit: "contain",
                                          display: "block",
                                          backgroundColor: "#fff",
                                          borderRadius: 1,
                                        }}
                                      />
                                    }
                                    arrow
                                    placement="right"
                                    componentsProps={{
                                      tooltip: {
                                        sx: {
                                          backgroundColor: "transparent",
                                          padding: 0,
                                          boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
                                          maxWidth: "none",
                                        },
                                      },
                                      arrow: {
                                        sx: {
                                          color: "#fff",
                                        },
                                      },
                                    }}
                                  >
                                    <img
                                      src={croquiUrl}
                                      alt={item.codigo}
                                      style={{ maxWidth: "100px", maxHeight: "50px", objectFit: "contain", cursor: "pointer" }}
                                      onError={(event) => {
                                        const target = event.currentTarget;
                                        target.style.display = "none";
                                        const placeholder = target.parentElement;
                                        if (placeholder) {
                                          placeholder.innerHTML = '<div style="color:#999;font-size:12px;">Sem imagem</div>';
                                        }
                                      }}
                                    />
                                  </Tooltip>
                                ) : (
                                  <div style={{ color: "#999", fontSize: "12px" }}>Sem imagem</div>
                                )}
                              </div>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2" fontWeight={600}>
                                {item.codigo}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: foiAtualizada ? 600 : 400,
                                  color: foiAtualizada ? "primary.main" : "text.primary",
                                }}
                              >
                                {decodeLegacyText(item.descricao) || "-"}
                              </Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2">{detalhe.projetistaNome}</Typography>
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2">{item.linha || "-"}</Typography>
                            </TableCell>

                            <TableCell>
                              <Chip label={getStatusLabel(item)} color={getStatusColor(item)} size="small" sx={{ fontWeight: 600 }} />
                            </TableCell>

                            <TableCell>
                              <Typography variant="body2">
                                {item.dataVersao ? formatoData(item.dataVersao) : "-"}
                              </Typography>
                            </TableCell>

                            {modoValidacao && (
                              <TableCell align="center">
                                <Stack direction="row" spacing={0.5} justifyContent="center">
                                  {modoValidacao && 
                                   !item.substituida && 
                                   getItemSituacao(item) !== 3 && 
                                   !detalhe?.congelada &&
                                   (cemTab === "cemMobi" ? detalhe?.situacaoCemMobi : detalhe?.situacaoCemDelphi) !== SolicitacaoTipologiaSituacao.DisponivelMercado && (
                                    <Tooltip title="Excluir do pacote (não será homologada)">
                                      <span>
                                        <IconButton
                                          size="small"
                                          color="error"
                                          disabled={removendoTipologia === item.idItem}
                                          onClick={() => solicitarConfirmacaoRemocao(item)}
                                        >
                                          <DeleteIcon fontSize="small" />
                                        </IconButton>
                                      </span>
                                    </Tooltip>
                                  )}
                                </Stack>
                              </TableCell>
                            )}
                          </TableRow>
                        </Tooltip>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>


      <Dialog
        open={confirmacaoRemocao.open}
        onClose={cancelarRemocaoTipologia}
        maxWidth="sm"
        fullWidth
      >
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <Typography variant="h6" fontWeight={600}>
              Excluir tipologia do pacote?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              A tipologia <strong>{confirmacaoRemocao.codigo}</strong>
              {confirmacaoRemocao.descricao && (
                <> ({decodeLegacyText(confirmacaoRemocao.descricao)})</>
              )}{" "}
              <strong>não será incluída no pacote de tipologias que será homologado</strong>.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Esta ação pode ser desfeita posteriormente caso necessário.
            </Typography>
          </Stack>
        </DialogContent>
        <Divider />
        <Box sx={{ p: 2, display: "flex", justifyContent: "flex-end", gap: 1.5 }}>
          <Button
            variant="outlined"
            color="inherit"
            onClick={cancelarRemocaoTipologia}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <LoadingButton
            variant="contained"
            color="error"
            loading={removendoTipologia === confirmacaoRemocao.itemId}
            onClick={confirmarRemocaoTipologia}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Confirmar Exclusão
          </LoadingButton>
        </Box>
      </Dialog>

      {/* Modal de confirmação de homologação */}
      <Dialog
        open={modalConfirmacaoHomologacaoOpen}
        onClose={() => setModalConfirmacaoHomologacaoOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: "#3C90AD", color: "#ffffff", py: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            Concluir Homologação
          </Typography>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={3}>
            {/* Cabeçalho com texto descritivo */}
            <Box sx={{ backgroundColor: "#EBF4F9", p: 2, borderRadius: 1 }}>
              <Typography variant="body1" color="text.primary" sx={{ mb: 2, fontStyle: "italic" }}>
                Ao confirmar a conclusão da homologação (Realização de todos os testes necessários) você autoriza que todas as tipologias abaixo sejam disponibilizadas para uso dos clientes nos softwares da Alumisoft.
              </Typography>
              
              {/* Campos: Sistemista, Linha, Quantidade */}
              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Sistemista:
                  </Typography>
                  <Box sx={{ backgroundColor: "#ffffff", p: 1, borderRadius: 1, border: "1px solid #e0e0e0" }}>
                    <Typography variant="body2" fontWeight={500}>
                      {detalhe?.projetistaNome || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Linha:
                  </Typography>
                  <Box sx={{ backgroundColor: "#ffffff", p: 1, borderRadius: 1, border: "1px solid #e0e0e0" }}>
                    <Typography variant="body2" fontWeight={500}>
                      {detalhe?.linhaSistema || "-"}
                    </Typography>
                  </Box>
                </Grid>
                <Grid item xs={12} sm={4}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5 }}>
                    Quantidade de tipologias:
                  </Typography>
                  <Box sx={{ backgroundColor: "#ffffff", p: 1, borderRadius: 1, border: "1px solid #e0e0e0" }}>
                    <Typography variant="body2" fontWeight={500}>
                      {detalhe?.tipologias?.filter(t => !t.substituida).length || 0}
                    </Typography>
                  </Box>
                </Grid>
              </Grid>
            </Box>

            {/* Instruções */}
            <Box>
              <Typography variant="subtitle2" fontWeight={600} color="primary" sx={{ mb: 1 }}>
                Instruções:
              </Typography>
              {/* Plano 05/10/2026 (Frente 4): texto das instruções do Concluir Homologação. */}
              <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
                Após a sua confirmação, em breve você receberá uma notificação por e-mail informando que a Alumisoft disponibilizou as tipologias para uso dos clientes. Se essa solicitação estiver vinculada a outras, todas precisam ter a homologação concluída para que fiquem disponíveis para uso dos clientes.
              </Typography>
            </Box>

            {/* Tabela de tipologias */}
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: "#3C90AD" }}>
                    <TableCell sx={{ color: "#ffffff", fontWeight: 600 }}>TIPOLOGIAS</TableCell>
                    <TableCell align="center" sx={{ color: "#ffffff", fontWeight: 600 }}>ATUALIZADA</TableCell>
                    <TableCell align="center" sx={{ color: "#ffffff", fontWeight: 600 }}>DATA</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {detalhe?.tipologias
                    ?.filter(t => !t.substituida)
                    .map((tipologia) => {
                      const dataFormatada = tipologia.dataVersao
                        ? formatoData(tipologia.dataVersao)
                        : "-";
                      return (
                        <TableRow key={tipologia.idItem}>
                          <TableCell>{tipologia.codigo}</TableCell>
                          <TableCell align="center">
                            <Checkbox checked={true} disabled size="small" />
                          </TableCell>
                          <TableCell align="center">{dataFormatada}</TableCell>
                        </TableRow>
                      );
                    }) || []}
                </TableBody>
              </Table>
            </TableContainer>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setModalConfirmacaoHomologacaoOpen(false)}
            color="inherit"
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Cancelar
          </Button>
          <LoadingButton
            onClick={confirmarHomologacao}
            variant="contained"
            color="primary"
            loading={processingSituacao === SolicitacaoTipologiaAcao.ConcluirHomologacao}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            Confirmar
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Modal de upload de HTML */}
      <Dialog
        open={modalUploadHtmlOpen}
        onClose={() => {
          if (!uploadingHtml) {
            setModalUploadHtmlOpen(false);
            setHtmlFile(null);
            setSistemaParaUpload(null);
          }
        }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: "#3C90AD", color: "#ffffff", py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography variant="h6" fontWeight={600}>
            Anexar informativo (HTML)
          </Typography>
          <IconButton
            onClick={() => {
              if (!uploadingHtml) {
                setModalUploadHtmlOpen(false);
                setHtmlFile(null);
                setSistemaParaUpload(null);
              }
            }}
            sx={{ color: "#ffffff" }}
            disabled={uploadingHtml}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Stack spacing={2}>
            <Typography variant="body2" color="text.secondary">
              Selecione o arquivo HTML referente à solicitação para {sistemaParaUpload === SolicitacaoSistema.CemDelphi ? "CEM Desktop" : "CEM MOBI"}.
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Button
                variant="contained"
                component="label"
                disabled={uploadingHtml}
                sx={{ textTransform: "none", fontWeight: 600, backgroundColor: "#3C90AD" }}
              >
                BUSCAR
                <input
                  type="file"
                  hidden
                  accept=".html"
                  onChange={handleFileChange}
                />
              </Button>
              <TextField
                fullWidth
                value={htmlFile?.name || ""}
                placeholder="nome do arquivo.html"
                disabled
                size="small"
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.875rem",
                  },
                }}
              />
            </Stack>
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => {
              if (!uploadingHtml) {
                setModalUploadHtmlOpen(false);
                setHtmlFile(null);
                setSistemaParaUpload(null);
              }
            }}
            disabled={uploadingHtml}
            sx={{ textTransform: "none", fontWeight: 600 }}
          >
            CANCELAR
          </Button>
          <LoadingButton
            onClick={handleUploadHtml}
            variant="contained"
            color="primary"
            loading={uploadingHtml}
            disabled={!htmlFile || uploadingHtml}
            sx={{ textTransform: "none", fontWeight: 600, backgroundColor: "#3C90AD" }}
          >
            CONFIRMAR
          </LoadingButton>
        </DialogActions>
      </Dialog>

      {/* Modal de confirmação de vínculos */}
      <Dialog
        open={modalConfirmacaoVinculosOpen}
        onClose={() => setModalConfirmacaoVinculosOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ backgroundColor: "#3C90AD", color: "#ffffff", py: 2 }}>
          Solicitações Vinculadas
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Typography>
            Todas as solicitações vinculadas ficarão disponíveis para uso dos clientes. Deseja continuar?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setModalConfirmacaoVinculosOpen(false)}>Não</Button>
          <Button variant="contained" onClick={handleConfirmacaoVinculos}>Sim</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SolicitacaoDetalhePage;

